<script setup>
import { computed, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useInvoices } from "../../Composables/useInvoices.js"
import { useAuthUserStore } from "../../Store/AuthUserStore.js"
import _ from 'lodash'
import axios from "axios"
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"
import FormSelectField from "../../Components/FormSelectField.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()
const authUserStore = useAuthUserStore()
const authUser = computed(() => authUserStore.authUser)

const storageKey = "Shiftech_InvoiceList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: invoices,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    search,
    loadData,
} = useDataManager({
    baseUrl: '/api/invoices',
    baseSearchUrl: '/api/search/invoices',
    defaultSortingField: 'created_at',
    defaultSortingFieldsOrder: {
        invoice_number: 'asc',
        created_at: 'asc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: 'Numéro facture', field: 'invoice_number', sortable: true, slot: 'invoice_number', class: 'col-1' },
    { label: 'Nom client', slot: 'client', class: 'col-2' },
    { label: 'Centre', slot: 'center', class: 'col-1' },
    { label: 'Status', slot: 'status', class: 'col-1' },
    { label: 'Date facturation', slot: 'invoice_date', class: 'col-1' },
    { label: 'Date échéance', slot: 'due_date', class: 'col-1' },
    { label: 'Total TTC', slot: 'total', class: 'col-1' },
    { label: 'Date création', field: 'created_at', sortable: true, slot: 'created_at', class: 'col-1' },
    { label: 'Détails', slot: 'details', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

const errors = ref(null)
const filters = reactive({
    center_id: null,
    status: null,
})
const filtersApplied = ref(true)
const isInitialized = ref(false)
const centers = ref([])

onBeforeMount(() => {
    getCenters()

    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        url.value = savedSettings.url || url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
        filtersApplied.value = savedSettings.filtersApplied ?? true

        if (savedSettings.filters) {
            Object.assign(filters, savedSettings.filters)
        }
    }

    loadData(url.value)
})

onMounted(() => {
    isInitialized.value = true
})

watch(
    [url, sortingField, sortingFieldsOrder, query, searchLoaded, filtersApplied, filters],
    ([newUrl, newSortingField, newSortingOrder, newQuery, newSearchLoaded, newFiltersApplied]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
            filtersApplied: newFiltersApplied,
            filters: { ...filters },
        })
    },
    { deep: true }
)

watch(
    () => [filters.center_id, filters.status],
    () => {
        if (isInitialized.value) {
            filtersApplied.value = false
        }
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

function applyFilter() {
    let newUrl = new URL(url.value, window.location.origin)

    Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== '') {
            newUrl.searchParams.set(key, value)
        } else {
            newUrl.searchParams.delete(key)
        }
    })

    filtersApplied.value = true
    loadData(newUrl.href)
}

function getCenters() {
    centers.value.push({
        text: 'Tous les centres',
        value: null
    })
    axios.get('/api/centers?sortingField=name&sortingOrder=asc&all==true')
        .then(response => {
            response.data.data.forEach(center => {
                centers.value.push({
                    text: center.data.name,
                    value: center.data.id
                })
            })
        })
        .catch(error => {
            httpErrorsHandler(error)
        })
}

const idToDelete = ref(null)

function showModal(modalId, id) {
    idToDelete.value = id
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

const {
    deleteInvoice,
} = useInvoices({
    onDeleted: () => {
        loadData(url.value)
        hideModal('modalDelete')
    },
    onError: (error) => httpErrorsHandler(error),
})
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header border border-dashed border-top-0 border-start-0 border-end-0">
                    <div class="d-flex align-items-center">
                        <h5 class="card-title mb-0 flex-grow-1">Toutes les factures</h5>
                        <router-link :to="{ name:'invoices.create' }" class="btn btn-secondary" v-if="authUser.can && authUser.can.create_invoices">
                            <i class="ri-add-fill me-1 align-bottom hide-on-mobile"></i> Ajouter
                        </router-link>
                    </div>
                </div>

                <!-- Filters -->
                <div class="card-body">
                    <div class="row g-3">
                        <div class="col-xxl-5">
                            <div class="search-box">
                                <input type="search" class="form-control search" placeholder="Rechercher..." v-model="query" @input="debouncedSearch" id="searchInvoices">
                                <i class="ri-search-line search-icon"></i>
                            </div>
                        </div>
                        <form-select-field
                            name="filters.status"
                            label="Statut"
                            :label-hidden="true"
                            @update:field="filters.status = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.status"
                            bs-class="col-xxl-3 col-sm-5"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Tous les statuts"
                            :liste-options="[
                                { text: 'Statuts ouverts (Brouillon, En attente de paiement...)', value: 'all_open' },
                                { text: 'Statuts fermés (Payé, Annulé...)', value: 'all_closed' },
                                { text: 'Tous les statuts', value: null },
                                { text: '---', value: null },
                                { text: 'Brouillon', value: 'draft' },
                                { text: 'En attente de paiement', value: 'waiting_for_payment' },
                                { text: 'Payé', value: 'paid' },
                                { text: 'Annulé', value: 'canceled' }
                            ]"
                            margin="mb-0"
                        />
                        <form-select-field
                            name="filters.center_id"
                            label="Centre"
                            :label-hidden="true"
                            @update:field="filters.center_id = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.center_id"
                            bs-class="col-xxl-3 col-sm-5"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Tous les centres"
                            :liste-options="centers"
                            margin="mb-0"
                        />
                        <div class="col-xxl-1 col-sm-2">
                            <button type="button" class="btn w-100" @click.prevent="applyFilter" :class="filtersApplied ? 'btn-dark' : 'btn-warning'">
                                <i class="ri-equalizer-fill me-1 align-bottom hide-on-mobile"></i>
                                Filtrer
                            </button>
                        </div>
                    </div>
                </div>

                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="invoices"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- Invoice number -->
                        <template #invoice_number="{ item }">
                            <span class="fw-medium text-dark">{{ item.data.invoice_number }}</span>
                        </template>

                        <!-- Client -->
                        <template #client="{ item }">
                            {{ item.data.client.data.last_name }} {{ item.data.client.data.first_name }}
                        </template>

                        <!-- Center -->
                        <template #center="{ item }">
                            {{ item.data.center.data.name }}
                        </template>

                        <!-- Status -->
                        <template #status="{ item }">
                            <span :class="'badge fs-11 bg-'+item.data.status_badge_color+'-subtle text-'+item.data.status_badge_color">
                                {{ item.data.status_label_fr }}
                            </span>
                        </template>

                        <!-- Invoice date -->
                        <template #invoice_date="{ item }">
                            {{ item.data.invoice_date_fr }}
                        </template>

                        <!-- Due date -->
                        <template #due_date="{ item }">
                            {{ item.data.due_date_fr }}
                        </template>

                        <!-- Total -->
                        <template #total="{ item }">
                            {{ item.data.total_price_with_tax }}
                        </template>

                        <!-- Created at -->
                        <template #created_at="{ item }">
                            {{ item.data.created_at_fr }}
                        </template>

                        <!-- Details -->
                        <template #details="{ item }">
                            <router-link :to="{ name: 'invoices.show', params: { id: item.data.id } }" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm">
                                Accéder
                            </router-link>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- UPDATE -->
                            <router-link :to="{ name: 'invoices.edit', params: { id: item.data.id } }" class="text-info" title="Modifier la facture" v-if="item.can.update">
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- DELETE -->
                            <a href="#" class="text-danger" title="Supprimer la facture" @click.prevent="showModal('modalDelete', item.data.id)" v-if="item.can.delete">
                                <i class="ri-delete-bin-3-fill fs-17 px-1"></i>
                            </a>
                            <i class="ri-delete-bin-3-fill fs-17 px-1 text-muted" v-else></i>
                        </template>
                    </data-table>

                    <modal id="modalDelete" title="Supprimer la facture ?" btn-class="btn-danger" btn-text="Supprimer" @click:button="deleteInvoice(idToDelete)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point de supprimer une facture. Continuer ?</p>
                        </template>
                    </modal>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
